import {
  McpServerAuthMode,
  McpServerSource,
  ToolMethod,
  UserInstallAuthState,
  UserVisibleMcpServer
} from '@/types/mcp'
import { compareUserVisibleMcpServers } from '@/utils/mcpServers'

export type McpServerCarouselPrimaryActionKind = 'install' | 'connect-oauth' | 'view-tools'

export interface McpServerCarouselItem {
  serverName: string
  title: string
  description: string
  source: McpServerSource
  installed: boolean
  enabled: boolean
  authMode?: McpServerAuthMode
  authState?: UserInstallAuthState
  authRequired: boolean
  url?: string
  repositoryUrl?: string
  licenseName?: string
  toolsList?: ToolMethod[]
  toolCount: number | null
  primaryActionKind: McpServerCarouselPrimaryActionKind
  primaryActionLabel: string
}

export function buildMcpServerCarouselItems(servers: UserVisibleMcpServer[]): McpServerCarouselItem[] {
  const externalServers = servers.filter((server) => server.source === 'external')
  const sourceServers = externalServers.length > 0
    ? externalServers
    : servers.filter((server) => server.source === 'platform')

  return sourceServers
    .slice()
    .sort(compareMcpCarouselServers)
    .map((server) => mapServerToCarouselItem(server))
}

function compareMcpCarouselServers(left: UserVisibleMcpServer, right: UserVisibleMcpServer): number {
  const priorityDifference = getMcpCarouselPriority(left) - getMcpCarouselPriority(right)
  if (priorityDifference !== 0) {
    return priorityDifference
  }

  return compareUserVisibleMcpServers(left, right)
}

function getMcpCarouselPriority(server: UserVisibleMcpServer): number {
  if (server.source !== 'external') {
    return 4
  }

  if (server.canInstall === true && server.installed !== true) {
    return 0
  }

  if (server.installed === true && server.authMode === 'oauth2' && server.authState !== 'connected') {
    return 1
  }

  if (server.installed === true && server.enabled === true) {
    return 2
  }

  return 3
}

function mapServerToCarouselItem(server: UserVisibleMcpServer): McpServerCarouselItem {
  const primaryAction = resolvePrimaryAction(server)

  return {
    serverName: server.name,
    title: server.displayName || server.name,
    description: server.description || 'No description provided.',
    source: server.source,
    installed: server.installed,
    enabled: server.enabled,
    authMode: server.authMode,
    authState: server.authState,
    authRequired: server.authRequired,
    url: normalizeOptionalString(server.url),
    repositoryUrl: normalizeOptionalString(server.repositoryUrl),
    licenseName: normalizeOptionalString(server.licenseName),
    toolsList: server.toolsList,
    toolCount: Array.isArray(server.toolsList) ? server.toolsList.length : null,
    primaryActionKind: primaryAction.kind,
    primaryActionLabel: primaryAction.label
  }
}

function resolvePrimaryAction(
  server: UserVisibleMcpServer
): { kind: McpServerCarouselPrimaryActionKind; label: string } {
  if (server.source === 'external' && server.canInstall === true && server.installed !== true) {
    return {
      kind: 'install',
      label: 'Install'
    }
  }

  if (server.authMode === 'oauth2' && server.authState !== 'connected') {
    return {
      kind: 'connect-oauth',
      label: server.authState === 'reauth_required' ? 'Reconnect OAuth' : 'Connect OAuth'
    }
  }

  return {
    kind: 'view-tools',
    label: 'View Tools'
  }
}

function normalizeOptionalString(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}
